import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '@/api';
import { cn } from '@/lib/cn';
import { BETSLIP_MODES, type BetslipMode } from './Betslip.modes';

export type BetslipPage = 'betslip' | 'my-bets';

export interface EmptyBetslipProps {
  /** Active page tab. Defaults to Bet Slip. */
  page?: BetslipPage;
  onPageChange?: (page: BetslipPage) => void;
  mode?: BetslipMode;
  onModeChange?: (mode: BetslipMode) => void;
  /** Shown on the Bet Slip tab when > 0. */
  selectionCount?: number;
  /** Shown on the My bets tab when > 0. */
  myBetsCount?: number;
  className?: string;
}

function CountBadge({ label }: { label: string }) {
  return (
    <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-action-primary px-1 text-[10px] font-bold text-text-on-action">
      {label}
    </span>
  );
}

/**
 * Desktop empty betslip rail — page tabs, type tabs, and empty copy
 * (Figma DesktopBetslip / Empty). My bets is hidden when logged out.
 */
export function EmptyBetslip({
  page: pageProp,
  onPageChange,
  mode: modeProp,
  onModeChange,
  selectionCount = 0,
  myBetsCount = 0,
  className,
}: EmptyBetslipProps) {
  const { t } = useTranslation();
  const { loggedIn } = useAuthSession();
  const [pageInternal, setPageInternal] = useState<BetslipPage>('betslip');
  const [modeInternal, setModeInternal] = useState<BetslipMode>('single');
  const page = pageProp ?? pageInternal;
  const mode = modeProp ?? modeInternal;
  const activePage = !loggedIn && page === 'my-bets' ? 'betslip' : page;

  useEffect(() => {
    if (!loggedIn && page === 'my-bets') {
      onPageChange?.('betslip');
      if (pageProp === undefined) setPageInternal('betslip');
    }
  }, [loggedIn, page, pageProp, onPageChange]);

  const setPage = (next: BetslipPage) => {
    onPageChange?.(next);
    if (pageProp === undefined) setPageInternal(next);
  };

  const setMode = (next: BetslipMode) => {
    onModeChange?.(next);
    if (modeProp === undefined) setModeInternal(next);
  };

  const pageTabs: { value: BetslipPage; label: string; count: number }[] = [
    {
      value: 'betslip',
      label: t('Betslip', { defaultValue: 'Bet Slip' }),
      count: selectionCount,
    },
    ...(loggedIn
      ? [
          {
            value: 'my-bets' as const,
            label: t('MyBets', { defaultValue: 'My bets' }),
            count: myBetsCount,
          },
        ]
      : []),
  ];

  return (
    <section
      aria-label={t('Betslip', { defaultValue: 'Bet Slip' })}
      className={cn('flex w-full flex-col gap-3', className)}
    >
      <div
        role="tablist"
        aria-label={t('BetslipPages', { defaultValue: 'Bet slip pages' })}
        className="flex h-11 items-end justify-center"
      >
        {pageTabs.map((tab) => {
          const active = activePage === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPage(tab.value)}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 overflow-hidden pt-2.5"
            >
              <span className="flex items-center justify-center gap-1.5">
                <span
                  className={cn(
                    'text-sm whitespace-nowrap',
                    active
                      ? 'font-bold text-text-primary'
                      : 'font-normal text-text-secondary',
                  )}
                >
                  {tab.label}
                </span>
                {tab.count > 0 ? <CountBadge label={String(tab.count)} /> : null}
              </span>
              <span
                aria-hidden
                className={cn(
                  'h-0.5 w-full',
                  active ? 'bg-action-primary' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>

      <div
        role="tablist"
        aria-label={t('BetType', { defaultValue: 'Bet type' })}
        className="flex items-start px-1"
      >
        {BETSLIP_MODES.map(({ value, label }) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setMode(value)}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 overflow-hidden pt-2"
            >
              <span
                className={cn(
                  'text-[13px] whitespace-nowrap lowercase',
                  active
                    ? 'font-bold text-text-primary'
                    : 'font-normal text-text-secondary',
                )}
              >
                {label}
              </span>
              <span
                aria-hidden
                className={cn(
                  'h-0.5 w-full',
                  active ? 'bg-action-primary' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>

      <p className="w-full text-[13px] leading-5 text-text-secondary">
        {t('EmptyBetslipMessage', {
          defaultValue: "You haven't selected any bets yet. Tap odds to add selections.",
        })}
      </p>
    </section>
  );
}
